import { useThree } from '@react-three/fiber'
import type { RefObject } from 'react'
import { useSpring } from 'react-spring'
import type { Vector3 } from 'three'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'

import { CAMERA_SLIDE_SPRING_CONFIG } from '../constants'

interface UseCameraSlideArgs {
  orbitRef: RefObject<OrbitControlsImpl>
  initTarget: Vector3
  onChange: (target: Vector3, position: Vector3) => void
  onRest: (position: Vector3) => void
}

const useCameraSlide = ({
  orbitRef,
  initTarget,
  onChange,
  onRest,
}: UseCameraSlideArgs) => {
  const { camera } = useThree()

  const [, cameraTweenApi] = useSpring(() => ({ progress: 0 }))

  const slideCameraTo = (target: Vector3) => {
    const controls = orbitRef.current
    if (!controls) {
      return
    }

    const fromTarget = controls.target.clone()
    const offset = camera.position.clone().sub(fromTarget)

    controls.enableRotate = false
    controls.enableZoom = false

    cameraTweenApi.start({
      from: { progress: 0 },
      to: { progress: 1 },
      config: CAMERA_SLIDE_SPRING_CONFIG,
      onChange: ({ value: { progress } }) => {
        const nextTarget = fromTarget.clone().lerp(target, progress)
        controls.target = nextTarget
        camera.position.copy(nextTarget).add(offset)
        onChange(controls.target, camera.position)
      },
      onRest: () => {
        controls.enableRotate = true
        controls.enableZoom = true
        onRest(camera.position.clone())
      },
    })
  }

  const slideCameraToInitialTarget = () => slideCameraTo(initTarget)

  return {
    slideCameraTo,
    slideCameraToInitialTarget,
  }
}

export default useCameraSlide
